/**
 * Parser para arquivos Excel (XLSX)
 * Extrai transações de planilhas
 */

import * as XLSX from 'xlsx';

export interface ExcelTransaction {
  data: string;
  descricao: string;
  valor: number;
  tipo: 'recebimento' | 'pagamento';
  categoria?: string;
}

export function parseExcel(arquivo: File): Promise<ExcelTransaction[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (evento) => {
      try {
        const dados = evento.target?.result as ArrayBuffer;
        const workbook = XLSX.read(dados, { type: 'array' });

        // Tomar a primeira planilha
        const nomePlanilha = workbook.SheetNames[0];
        const planilha = workbook.Sheets[nomePlanilha];

        // Converter para JSON
        const linhas = XLSX.utils.sheet_to_json(planilha, { header: 1 }) as any[];

        const transacoes = parseLinhas(linhas);
        resolve(transacoes);
      } catch (erro) {
        reject(new Error(`Erro ao ler arquivo Excel: ${erro}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    reader.readAsArrayBuffer(arquivo);
  });
}

function parseLinhas(linhas: any[]): ExcelTransaction[] {
  const transacoes: ExcelTransaction[] = [];

  // Detectar cabeçalhos e índices de colunas
  let indiceData = -1;
  let indiceDescricao = -1;
  let indiceValor = -1;
  let indiceTipo = -1;
  let indiceCategoria = -1;

  // Procurar por cabeçalhos na primeira linha
  if (linhas.length > 0) {
    const cabecalho = linhas[0];

    for (let i = 0; i < cabecalho.length; i++) {
      const valor = String(cabecalho[i]).toLowerCase().trim();

      if (['data', 'date', 'data_transacao'].includes(valor)) {
        indiceData = i;
      } else if (['descricao', 'description', 'descrição', 'transacao'].includes(valor)) {
        indiceDescricao = i;
      } else if (['valor', 'amount', 'montante', 'valor_total'].includes(valor)) {
        indiceValor = i;
      } else if (['tipo', 'type', 'tipo_transacao', 'natureza'].includes(valor)) {
        indiceTipo = i;
      } else if (['categoria', 'category', 'categoria_despesa'].includes(valor)) {
        indiceCategoria = i;
      }
    }

    // Se não encontrou cabeçalhos, assumir posições padrão
    if (indiceData === -1) indiceData = 0;
    if (indiceDescricao === -1) indiceDescricao = 1;
    if (indiceValor === -1) indiceValor = 2;
    if (indiceTipo === -1) indiceTipo = 3;

    // Processar dados (começar da linha 2 se houver cabeçalho)
    const startLine = linhas.length > 0 && typeof linhas[0][0] === 'string' && linhas[0][0].toLowerCase().includes('data') ? 1 : 0;

    for (let i = startLine; i < linhas.length; i++) {
      const linha = linhas[i];
      if (!linha || !linha[indiceData]) continue;

      try {
        const transacao = parseLinhaExcel(linha, {
          indiceData,
          indiceDescricao,
          indiceValor,
          indiceTipo,
          indiceCategoria,
        });

        if (transacao) {
          transacoes.push(transacao);
        }
      } catch (erro) {
        console.warn(`Erro ao processar linha ${i}:`, erro);
      }
    }
  }

  // Ordenar por data
  transacoes.sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());

  return transacoes;
}

function parseLinhaExcel(
  linha: any[],
  indices: {
    indiceData: number;
    indiceDescricao: number;
    indiceValor: number;
    indiceTipo: number;
    indiceCategoria?: number;
  }
): ExcelTransaction | null {
  try {
    // Extrair data
    let data = linha[indices.indiceData];
    if (!data) return null;

    // Se for número (data serial do Excel), converter
    if (typeof data === 'number') {
      const dataObj = new Date((data - 25569) * 86400 * 1000);
      data = dataObj.toISOString().split('T')[0];
    } else {
      // Tentar parsear como string
      data = formatarData(String(data));
    }

    // Extrair descrição
    const descricao = String(linha[indices.indiceDescricao] || 'Transação').trim();

    // Extrair valor
    let valor = linha[indices.indiceValor];
    if (typeof valor === 'string') {
      valor = parseFloat(valor.replace(/[^0-9.-]/g, ''));
    }
    valor = Math.abs(valor || 0);

    if (valor === 0) return null;

    // Extrair tipo
    let tipo: 'recebimento' | 'pagamento' = 'pagamento';
    const tipoStr = String(linha[indices.indiceTipo] || '').toLowerCase().trim();

    if (tipoStr.includes('entrada') || tipoStr.includes('receb') || tipoStr.includes('crédito') || tipoStr.includes('in')) {
      tipo = 'recebimento';
    }

    // Extrair categoria
    const categoria = indices.indiceCategoria !== undefined ? String(linha[indices.indiceCategoria] || '').trim() : undefined;

    return {
      data,
      descricao,
      valor,
      tipo,
      categoria: categoria || undefined,
    };
  } catch (erro) {
    console.warn('Erro ao processar linha Excel:', erro);
    return null;
  }
}

function formatarData(dataStr: string): string {
  // Tentar vários formatos
  const formatos = [
    /^(\d{4})-(\d{2})-(\d{2})/, // YYYY-MM-DD
    /^(\d{2})\/(\d{2})\/(\d{4})/, // DD/MM/YYYY
    /^(\d{2})-(\d{2})-(\d{4})/, // DD-MM-YYYY
    /^(\d{4})\/(\d{2})\/(\d{2})/, // YYYY/MM/DD
  ];

  for (const formato of formatos) {
    const match = dataStr.match(formato);
    if (match) {
      if (match[3].length === 4) {
        // Formato YYYY-MM-DD ou YYYY/MM/DD
        return `${match[1]}-${String(match[2]).padStart(2, '0')}-${String(match[3]).padStart(2, '0')}`;
      } else {
        // Formato DD/MM/YYYY ou DD-MM-YYYY
        return `${match[3]}-${String(match[2]).padStart(2, '0')}-${String(match[1]).padStart(2, '0')}`;
      }
    }
  }

  // Se não conseguir parsear, retornar como está
  return dataStr;
}

/**
 * Valida um arquivo Excel
 */
export function isValidExcel(arquivo: File): boolean {
  const extensao = arquivo.name.toLowerCase();
  return extensao.endsWith('.xlsx') || extensao.endsWith('.xls');
}
